import type { OverviewResponse } from "contracts/api/dashboard/responses";
import { pathWithoutQuery } from "../../boundaries/url-path.parser";
import { clusterSort } from "./estateFormat";
import { CLUSTER_LAYER_GAP, CLUSTER_MARGIN, type Page } from "./estateTypes";
import { clusterContextForPages, clusterKey } from "./scanModel";

export type ClusterMetric = {
  key: string;
  label: string;
  allPages: Page[];
  visiblePages: Page[];
  columns: number;
  rows: number;
  width: number;
  height: number;
};

type ClusterPlacement = {
  metric: ClusterMetric;
  x: number;
  y: number;
};

export function layeredClusterCanvas(
  data: OverviewResponse,
  metrics: ClusterMetric[],
  viewportWidth: number,
): { placements: ClusterPlacement[]; width: number; height: number } {
  if (metrics.length === 0) {
    return { placements: [], width: 1, height: 1 };
  }
  const levels = clusterLevels(
    data,
    metrics.map((metric) => metric.key),
  );
  const byLevel = new Map<number, ClusterMetric[]>();
  for (const metric of metrics) {
    const level = levels.get(metric.key) ?? 0;
    byLevel.set(level, [...(byLevel.get(level) ?? []), metric]);
  }
  const maxWidth = viewportWidth - CLUSTER_MARGIN * 2;
  const rows = [...byLevel.entries()]
    .sort(([left], [right]) => left - right)
    .flatMap(([, levelMetrics]) =>
      packRows(
        levelMetrics.sort((left, right) => clusterSort(left.key, right.key)),
        maxWidth,
      ),
    );
  const rowWidths = rows.map(rowWidth);
  const rowHeights = rows.map((row) =>
    Math.max(...row.map((metric) => metric.height), 1),
  );
  const contentWidth = Math.max(...rowWidths, 1);
  const placements: ClusterPlacement[] = [];
  let cursorY = CLUSTER_MARGIN;
  rows.forEach((row, rowIndex) => {
    const rowHeight = rowHeights[rowIndex];
    let cursorX = CLUSTER_MARGIN + (contentWidth - rowWidths[rowIndex]) / 2;
    for (const metric of row) {
      placements.push({
        metric,
        x: cursorX,
        y: cursorY + (rowHeight - metric.height) / 2,
      });
      cursorX += metric.width + CLUSTER_LAYER_GAP;
    }
    cursorY += rowHeight + CLUSTER_LAYER_GAP;
  });
  return {
    placements,
    width: contentWidth + CLUSTER_MARGIN * 2,
    height: cursorY - CLUSTER_LAYER_GAP + CLUSTER_MARGIN,
  };
}

export function groupedPageCanvas(metrics: ClusterMetric[]): {
  placements: ClusterPlacement[];
  width: number;
  height: number;
} {
  if (metrics.length === 0) {
    return { placements: [], width: 1, height: 1 };
  }
  const columnCount = metrics.length === 1 ? 1 : metrics.length <= 4 ? 2 : 3;
  const columnSize = Math.ceil(metrics.length / columnCount);
  const columns = Array.from({ length: columnCount }, (_, index) =>
    metrics.slice(index * columnSize, (index + 1) * columnSize),
  ).filter((column) => column.length > 0);
  const columnWidths = columns.map((column) =>
    Math.max(...column.map((metric) => metric.width), 1),
  );
  const columnHeights = columns.map(
    (column) =>
      column.reduce((height, metric) => height + metric.height, 0) +
      Math.max(0, column.length - 1) * CLUSTER_LAYER_GAP,
  );
  const contentWidth =
    columnWidths.reduce((width, value) => width + value, 0) +
    Math.max(0, columns.length - 1) * CLUSTER_LAYER_GAP;
  const contentHeight = Math.max(...columnHeights, 1);
  const placements: ClusterPlacement[] = [];
  let cursorX = CLUSTER_MARGIN;
  columns.forEach((column, columnIndex) => {
    let cursorY =
      CLUSTER_MARGIN + (contentHeight - columnHeights[columnIndex]) / 2;
    for (const metric of column) {
      placements.push({
        metric,
        x: cursorX + (columnWidths[columnIndex] - metric.width) / 2,
        y: cursorY,
      });
      cursorY += metric.height + CLUSTER_LAYER_GAP;
    }
    cursorX += columnWidths[columnIndex] + CLUSTER_LAYER_GAP;
  });
  return {
    placements,
    width: contentWidth + CLUSTER_MARGIN * 2,
    height: contentHeight + CLUSTER_MARGIN * 2,
  };
}

function clusterLevels(
  data: OverviewResponse,
  clusterKeys: string[],
): Map<string, number> {
  const clusterContext = clusterContextForPages(data.pages);
  const pageCluster = new Map(
    data.pages.map((page) => [page.id, clusterKey(page.path, clusterContext)]),
  );
  const adjacency = new Map(clusterKeys.map((key) => [key, new Set<string>()]));
  for (const link of data.links) {
    const from = pageCluster.get(link.fromPageId);
    const to = pageCluster.get(link.toPageId);
    if (
      !from ||
      !to ||
      from === to ||
      !adjacency.has(from) ||
      !adjacency.has(to)
    ) {
      continue;
    }
    adjacency.get(from)?.add(to);
    adjacency.get(to)?.add(from);
  }
  const root =
    data.pages.find((page) => pathWithoutQuery(page.path) === "/") ??
    data.pages
      .filter((page) => clusterKey(page.path, clusterContext) === "home")
      .sort((left, right) => left.path.localeCompare(right.path))[0];
  const rootKey = root ? clusterKey(root.path, clusterContext) : clusterKeys[0];
  const levels = new Map<string, number>([[rootKey, 0]]);
  const queue = [rootKey];
  while (queue.length > 0) {
    const current = queue.shift();
    if (!current) continue;
    for (const neighbour of adjacency.get(current) ?? []) {
      if (!levels.has(neighbour)) {
        levels.set(neighbour, (levels.get(current) ?? 0) + 1);
        queue.push(neighbour);
      }
    }
  }
  const fallbackLevel = Math.max(...levels.values(), 0) + 1;
  for (const key of clusterKeys) {
    if (!levels.has(key)) levels.set(key, fallbackLevel);
  }
  return levels;
}

function packRows(
  metrics: ClusterMetric[],
  maxWidth: number,
): ClusterMetric[][] {
  const rows: ClusterMetric[][] = [];
  let row: ClusterMetric[] = [];
  for (const metric of metrics) {
    const next = [...row, metric];
    if (row.length > 0 && rowWidth(next) > maxWidth) {
      rows.push(row);
      row = [metric];
    } else {
      row = next;
    }
  }
  if (row.length > 0) rows.push(row);
  return rows;
}

function rowWidth(row: ClusterMetric[]): number {
  return (
    row.reduce((width, metric) => width + metric.width, 0) +
    Math.max(0, row.length - 1) * CLUSTER_LAYER_GAP
  );
}
